// store/slices/fileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  uploadFile,
  processUploadedFile,
  applyModels,
} from "../../services/fileService";

export const uploadFileThunk = createAsyncThunk(
  "file/upload",
  async (fileData, { rejectWithValue }) => {
    try {
      const response = await uploadFile(fileData);
      const raw = response;
      // console.log("raw>>>>>>>>>>>>>>>>>", raw);
      const filePath =
        raw?.file_path || raw?.filePath || raw?.data?.file_path || null;
      const lastResponse = raw?.data ?? raw;
      // console.log("Upload response:", raw, filePath, lastResponse);
      return { raw, filePath, lastResponse };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Upload failed"
      );
    }
  }
);

// second step thunk
export const processFileThunk = createAsyncThunk(
  "file/process",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await processUploadedFile(payload);
      // console.log("Process response:", response);
      const processResponse = response;
      return processResponse;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Process failed");
    }
  }
);

// third step thunk to apply models
export const applyModelsThunk = createAsyncThunk(
  "file/applyModels",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await applyModels(payload);
      // console.log("Apply models response:", data);
      return data ?? {};
    } catch (error) {
      const serverPayload = error?.response?.data ?? null;
      const message =
        serverPayload?.message || error?.message || "Apply models failed";
      return rejectWithValue({ message, serverPayload });
    }
  }
);

const initialState = {
    files: [],
    loading: false,
    error: null,
    lastResponse: null,
    uploadedFilePath: null,
    processing: false,
    processError: null,
    processResponse: null,
    applying: false,
    applyError: null,
    applyResponse: null,

    activeStep: 0, // 0 = not started, 1..N = step index
    totalSteps: 4, // default number of steps for processing
};

const fileSlice = createSlice({
  name: "file",
  initialState,
  reducers: {
    clearUploadState(state) {
      state.uploadedFilePath = null;
      state.lastResponse = null;
      state.error = null;
      state.applyResponse = null;
      state.processError = null;
      state.processing = false;
      state.activeStep = 0;
    },
    resetAll() {
      return initialState;
    },

    // NEW actions to control steps
    setActiveStep(state, action) {
      state.activeStep = action.payload;
    },
    setTotalSteps(state, action) {
      state.totalSteps = action.payload;
    },
    resetSteps(state) {
      state.activeStep = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // upload
      .addCase(uploadFileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastResponse = null;
        state.uploadedFilePath = null;
        state.processError = null;
        state.activeStep = 0;
      })
      .addCase(uploadFileThunk.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};

        // normalized fields
        state.lastResponse = payload.lastResponse ?? null;
        state.uploadedFilePath = payload.filePath ?? null;

        // --- STORE ONLY SERIALIZABLE DATA HERE ---
        state.files.push({
          filePath: payload.filePath ?? null,
          uploadedAt: new Date().toISOString(),
        });

        state.activeStep = 1;
      })
      .addCase(uploadFileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Upload failed";
        state.uploadedFilePath = null;
        state.activeStep = 0;
      })

      // process
      .addCase(processFileThunk.pending, (state) => {
        state.processing = true;
        state.processError = null;
        state.processResponse = null;
        if (state.activeStep < 2) state.activeStep = 2;
      })
      .addCase(processFileThunk.fulfilled, (state, action) => {
        state.processing = false;
        state.processResponse = action.payload || null;
        state.activeStep = state.totalSteps;
      })
      .addCase(processFileThunk.rejected, (state, action) => {
        state.processing = false;
        state.processError = action.payload || "Process failed";
      })

      // APPLY MODELS
      .addCase(applyModelsThunk.pending, (state) => {
        state.applying = true;
        state.applyError = null;
        state.applyResponse = null;
      })
      .addCase(applyModelsThunk.fulfilled, (state, action) => {
        state.applying = false;
        // action.payload expected to be the server response object:
        // { file_name, applied_models, blob_download_link }
        state.applyResponse = action.payload ?? null;
      })
      .addCase(applyModelsThunk.rejected, (state, action) => {
        state.applying = false;
        state.applyError = action.payload || "Apply models failed";
      });
  },
});

export const { clearUploadState, resetAll, setActiveStep, setTotalSteps, resetSteps } =
  fileSlice.actions;
export default fileSlice.reducer;
